import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getPathWithRef } from '../utils/attribution';
import { showPopupMessage } from '../utils/showPopup';
import LanguageSwitch from '../components/LanguageSwitch';
import DataPanel from '../components/DataPanel';
import FileUpload from '../components/FileUpload';
import FormViewer from '../components/FormViewer';
import InteractiveForm from '../components/InteractiveForm';
import './Chat.css';

const APPLICATION_FORM_ID = 'UNIFIED';

/**
 * Chat page: form-filling chat + data/progress sidebar (OG parity) + optional live handoff.
 * Session is created when user first opens Dashboard and clicks Continue; we reuse it here.
 */
export default function Chat() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('immigrationSessionId'));
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [liveChatSession, setLiveChatSession] = useState(null);
  const [chatStatus, setChatStatus] = useState('ai_only');
  const [queuePosition, setQueuePosition] = useState(null);
  const [isRequestingAgent, setIsRequestingAgent] = useState(false);
  const [collectedData, setCollectedData] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [formFields, setFormFields] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFormViewer, setShowFormViewer] = useState(false);
  /** 'chat' = chat expanded / form collapsed; 'form' = form expanded / chat collapsed */
  const [viewMode, setViewMode] = useState('chat');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isSubmittingRef = useRef(false);

  const loadMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await mvpApi.getMessages(sessionId, 100);
      if (response.data && response.data.success && response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  }, [sessionId]);

  const loadSessionData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await mvpApi.getCollectedData(sessionId, APPLICATION_FORM_ID);
      const data = response.data;
      if (data) {
        setCollectedData(data.collected_fields || {});
        setMissingFields(data.missing_fields || []);
        setCompletionPercentage(data.completion_percentage ?? 0);
      }
    } catch (err) {
      console.error('Error loading session data:', err);
    }
  }, [sessionId]);

  const loadFormFields = useCallback(async () => {
    const lang = localStorage.getItem('i18nextLng') || 'es';
    try {
      const response = await mvpApi.getFormFields(APPLICATION_FORM_ID, lang);
      if (response.data?.fields) {
        setFormFields(response.data.fields);
      }
    } catch (err) {
      console.error('Error loading form fields:', err);
    }
  }, []);

  // Keep backend session language in sync with current UI language
  useEffect(() => {
    if (!sessionId) return;
    const lang = i18n.language?.split('-')[0] || 'es';
    mvpApi
      .updateLanguage(sessionId, lang)
      .catch((err) => console.error('Error updating session language:', err));
  }, [i18n.language, sessionId]);

  const loadLiveChatMessages = useCallback(async () => {
    if (!liveChatSession) return;
    try {
      const res = await mvpApi.getLiveChatMessages(liveChatSession.id);
      if (res.data?.messages?.length) {
        const liveMessages = res.data.messages.map((m) => ({
          role: m.sender_type === 'agent' ? 'agent' : 'user',
          content: m.content,
          timestamp: m.created_at,
        }));
        setMessages((prev) => {
          const seen = new Set(prev.map((p) => `${p.content}|${p.timestamp}`));
          const newOnes = liveMessages.filter((m) => !seen.has(`${m.content}|${m.timestamp}`));
          return newOnes.length ? [...prev, ...newOnes] : prev;
        });
      }
    } catch (err) {
      console.error('Error loading live chat messages:', err);
    }
  }, [liveChatSession]);

  const pollQueueStatus = useCallback((session) => {
    const lc = session || liveChatSession;
    if (!lc) return;
    const interval = setInterval(async () => {
      try {
        const sessionResponse = await mvpApi.getLiveChatSession(lc.id);
        if (sessionResponse.data) {
          const s = sessionResponse.data;
          setChatStatus(s.status);
          setLiveChatSession(s);
          if (s.status === 'human_active') {
            setChatStatus('agent_assigned');
            setLiveChatSession(s);
            clearInterval(interval);
            const msgRes = await mvpApi.getLiveChatMessages(s.id);
            if (msgRes.data?.messages?.length) {
              const liveMessages = msgRes.data.messages.map((m) => ({
                role: m.sender_type === 'agent' ? 'agent' : 'user',
                content: m.content,
                timestamp: m.created_at,
              }));
              setMessages((prev) => {
                const seen = new Set(prev.map((p) => `${p.content}|${p.timestamp}`));
                const newOnes = liveMessages.filter((m) => !seen.has(`${m.content}|${m.timestamp}`));
                return newOnes.length ? [...prev, ...newOnes] : prev;
              });
            }
            return;
          }
          const queueRes = await mvpApi.getLiveChatQueueStatus(lc.id);
          if (queueRes.data?.position != null) setQueuePosition(queueRes.data.position);
        }
      } catch (err) {
        console.error('Error polling queue status:', err);
        clearInterval(interval);
      }
    }, 3000);
    setTimeout(() => clearInterval(interval), 300000);
  }, [liveChatSession]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate(getPathWithRef('/'), { replace: true });
      return;
    }
    if (!sessionId) {
      mvpApi
        .startSession({
          language: localStorage.getItem('i18nextLng') || 'es',
          form_type: 'UNIFIED',
        })
        .then((res) => {
          if (res.data && res.data.success) {
            const sid = res.data.session_id;
            setSessionId(sid);
            localStorage.setItem('immigrationSessionId', sid);
            loadMessages();
            loadSessionData();
            loadFormFields();
          }
        })
        .catch(() => navigate('/dashboard', { replace: true }));
    } else {
      loadMessages();
      loadSessionData();
      loadFormFields();
    }
  }, [sessionId, navigate, loadMessages, loadSessionData, loadFormFields]);

  useEffect(() => {
    loadFormFields();
    loadSessionData();
  }, [i18n.language, loadFormFields, loadSessionData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // When in agent mode, poll for new messages so agent replies appear on the user's screen
  useEffect(() => {
    if (chatStatus !== 'agent_assigned' || !liveChatSession) return;
    const interval = setInterval(async () => {
      try {
        const res = await mvpApi.getLiveChatMessages(liveChatSession.id);
        if (res.data?.messages?.length) {
          const liveMessages = res.data.messages.map((m) => ({
            role: m.sender_type === 'agent' ? 'agent' : 'user',
            content: m.content,
            timestamp: m.created_at,
          }));
          setMessages((prev) => {
            const seen = new Set(prev.map((p) => `${p.content}|${p.timestamp}`));
            const newOnes = liveMessages.filter((m) => !seen.has(`${m.content}|${m.timestamp}`));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
        }
      } catch (err) {
        console.error('Error polling live chat messages:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [chatStatus, liveChatSession]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || isLoading || !inputValue.trim()) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    isSubmittingRef.current = true;
    setIsLoading(true);
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: userMessage, timestamp: new Date().toISOString() },
    ]);
    try {
      if (chatStatus === 'agent_assigned' && liveChatSession) {
        await mvpApi.sendLiveChatMessage(liveChatSession.id, userMessage);
        const res = await mvpApi.getLiveChatMessages(liveChatSession.id);
        if (res.data?.messages?.length) {
          const liveMessages = res.data.messages.map((m) => ({
            role: m.sender_type === 'agent' ? 'agent' : 'user',
            content: m.content,
            timestamp: m.created_at,
          }));
          setMessages((prev) => {
            const seen = new Set(prev.map((p) => `${p.content}|${p.timestamp}`));
            const newOnes = liveMessages.filter((m) => !seen.has(`${m.content}|${m.timestamp}`));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
        }
      } else {
        const response = await mvpApi.sendMessage(sessionId, userMessage);
        const data = response?.data;
        if (data?.escalation_detected && data?.live_chat_session_id != null) {
          setQueuePosition(data.queue_position ?? null);
          try {
            const liveChatResponse = await mvpApi.getLiveChatSession(data.live_chat_session_id);
            if (liveChatResponse.data) {
              const session = liveChatResponse.data;
              setLiveChatSession(session);
              setChatStatus(session.status === 'human_active' ? 'agent_assigned' : session.status);
              if (session.status === 'queued' || session.status === 'handoff_requested') {
                pollQueueStatus(session);
              } else if (session.status === 'human_active') {
                const msgRes = await mvpApi.getLiveChatMessages(session.id);
                if (msgRes.data?.messages?.length) {
                  const liveMessages = msgRes.data.messages.map((m) => ({
                    role: m.sender_type === 'agent' ? 'agent' : 'user',
                    content: m.content,
                    timestamp: m.created_at,
                  }));
                  setMessages((prev) => [...prev, ...liveMessages]);
                }
              }
            }
          } catch (err) {
            console.error('Error loading live chat session after escalation:', err);
          }
          await loadMessages();
        } else {
          await loadMessages();
          await loadSessionData();
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages((prev) => prev.slice(0, -1));
      showPopupMessage(t('chat.errorSendingMessage') || 'Error sending message. Please try again.', 'error');
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleFieldEdit = useCallback(
    async (field, value) => {
      if (!sessionId) return;
      try {
        await mvpApi.updateField(sessionId, field, value);
        await loadSessionData();
      } catch (err) {
        console.error('Error updating field:', err);
      }
    },
    [sessionId, loadSessionData]
  );

  const handleExport = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await mvpApi.exportData(sessionId, 'json');
      const data = response.data?.data || response.data;
      if (data) {
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `immigration-data-${sessionId.slice(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting data:', err);
    }
  }, [sessionId]);

  const handleFileUpload = useCallback(
    async (file) => {
      if (!sessionId) return;
      try {
        await mvpApi.uploadDocument(sessionId, file);
        setShowUploadModal(false);
        await loadSessionData();
        await loadMessages();
      } catch (err) {
        console.error('Error uploading file:', err);
        const d = err?.response?.data?.detail;
        const extra =
          err?.response?.status === 503 && typeof d === 'string' ? `\n\n${d}` : '';
        showPopupMessage((t('upload.errorUploading') || 'Error uploading file. Please try again.') + extra, 'error');
      }
    },
    [sessionId, loadSessionData, loadMessages, t]
  );

  const handleRequestAgent = async () => {
    if (isRequestingAgent) return;
    try {
      setIsRequestingAgent(true);
      let lc = liveChatSession;
      if (!lc) {
        const startRes = await mvpApi.startLiveChatSession();
        lc = startRes.data.session;
        setLiveChatSession(lc);
      }
      const handoffRes = await mvpApi.requestLiveChatHandoff(
        lc.id,
        'User requested to speak with human agent'
      );
      setChatStatus(handoffRes.data.session?.status || 'queued');
      setQueuePosition(handoffRes.data.queue_position);
      if (handoffRes.data.session) setLiveChatSession(handoffRes.data.session);
      if (handoffRes.data.session?.status === 'human_active') {
        setChatStatus('agent_assigned');
        const msgRes = await mvpApi.getLiveChatMessages(handoffRes.data.session.id);
        if (msgRes.data?.messages) {
          const liveMessages = msgRes.data.messages.map((m) => ({
            role: m.sender_type === 'agent' ? 'agent' : 'user',
            content: m.content,
            timestamp: m.created_at,
          }));
          setMessages((prev) => [...prev, ...liveMessages]);
        }
      }
    } catch (err) {
      console.error('Error requesting agent:', err);
      showPopupMessage('Failed to request agent. Please try again.', 'error');
    } finally {
      setIsRequestingAgent(false);
    }
  };

  return (
    <div className="mvp-chat-page">
      <header className="mvp-chat-header">
        <button
          type="button"
          className="mvp-chat-back"
          onClick={() => navigate('/dashboard')}
          aria-label={t('chat.backToDashboard')}
        >
          ← {t('back')}
        </button>
        <div className="mvp-chat-title">{t('chat.title')}</div>
        <LanguageSwitch />
        <div className="mvp-chat-view-toggle" role="tablist" aria-label={t('chat.viewToggle') || 'Switch between chat and form'}>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'chat'}
            className={`mvp-chat-view-tab ${viewMode === 'chat' ? 'mvp-chat-view-tab--active' : ''}`}
            onClick={() => setViewMode('chat')}
          >
            {t('chat.viewChat') || 'Chat'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'form'}
            className={`mvp-chat-view-tab ${viewMode === 'form' ? 'mvp-chat-view-tab--active' : ''}`}
            onClick={() => setViewMode('form')}
          >
            {t('chat.viewForm') || 'Form'} ({Math.round(completionPercentage)}%)
          </button>
        </div>
        {/* Talk to human / agent handoff - commented out
        {chatStatus === 'ai_only' && (
          <button
            type="button"
            className="mvp-chat-handoff"
            onClick={handleRequestAgent}
            disabled={isRequestingAgent}
          >
            {isRequestingAgent ? '…' : t('chat.talkToHuman')}
          </button>
        )}
        */}
        {(chatStatus === 'queued' || chatStatus === 'handoff_requested') && (
          <span className="mvp-chat-queue">{t('chat.queue', { position: queuePosition ?? '…' })}</span>
        )}
        {chatStatus === 'agent_assigned' && (
          <span className="mvp-chat-agent">{t('chat.agentConnected')}</span>
        )}
      </header>

      <div className={`mvp-chat-layout mvp-chat-layout--${viewMode}`}>
        <div className="mvp-chat-main" aria-hidden={viewMode !== 'chat'}>
          <div className="mvp-chat-messages">
            {messages.length === 0 && (
              <div className="mvp-chat-empty">{t('chat.empty')}</div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`mvp-chat-msg mvp-chat-msg--${msg.role}`}>
                <div className="mvp-chat-msg-content">{msg.content}</div>
                {msg.timestamp && (
                  <div className="mvp-chat-msg-time">
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="mvp-chat-form">
            <button
              type="button"
              className="mvp-chat-attach"
              onClick={() => setShowUploadModal(true)}
              aria-label={t('upload.uploadFile')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('chat.placeholder')}
              className="mvp-chat-input"
              disabled={isLoading}
            />
            <button
              type="submit"
              className="mvp-chat-send"
              disabled={isLoading || !inputValue.trim()}
              aria-label="Send"
            >
              {isLoading ? '…' : '➤'}
            </button>
          </form>
        </div>

        <aside className="mvp-chat-sidebar" aria-hidden={viewMode !== 'form'}>
          <div className="mvp-chat-form-switch">
            <span className="mvp-form-badge">{t('form.applicationFormLabel')}</span>
          </div>
          <DataPanel
              collectedData={collectedData}
              missingFields={missingFields}
              completionPercentage={completionPercentage}
              onFieldEdit={handleFieldEdit}
              onExport={handleExport}
              onViewForm={() => setShowFormViewer(true)}
              formType={APPLICATION_FORM_ID}
            />
          <InteractiveForm
            formType={APPLICATION_FORM_ID}
            fields={formFields}
            values={collectedData}
            onFieldUpdate={(field, value) => handleFieldEdit(field, value)}
          />
        </aside>
      </div>

      {showUploadModal && (
        <FileUpload
          onUpload={handleFileUpload}
          onClose={() => setShowUploadModal(false)}
        />
      )}
      {showFormViewer && sessionId && (
        <FormViewer
          sessionId={sessionId}
          onClose={() => setShowFormViewer(false)}
        />
      )}
    </div>
  );
}
